import { TCModel, TCString, GVL, PurposeRestriction, RestrictionType } from "@iabtechlabtcf/core";
import type { CmpConfigPayload, ConsentState } from "./types";
import { NO_LI_PURPOSE_IDS } from "./types";
import { computeDisclosure } from "./disclosure";

const RESTRICTION_MAP: Record<string, RestrictionType> = {
  not_allowed: RestrictionType.NOT_ALLOWED,
  "0": RestrictionType.NOT_ALLOWED,
  require_consent: RestrictionType.REQUIRE_CONSENT,
  consent: RestrictionType.REQUIRE_CONSENT,
  "1": RestrictionType.REQUIRE_CONSENT,
  require_li: RestrictionType.REQUIRE_LI,
  li: RestrictionType.REQUIRE_LI,
  "2": RestrictionType.REQUIRE_LI
};

export function buildTcString(config: CmpConfigPayload, state: ConsentState, consentScreen = 1): string {
  const gvl = new GVL(toGvlJson(config) as never);
  const model = new TCModel(gvl);
  model.cmpId = config.cmpId;
  model.cmpVersion = config.cmpVersion;
  model.consentScreen = consentScreen;
  model.consentLanguage = (config.language || "de").slice(0, 2).toLowerCase();
  model.isServiceSpecific = true;
  model.purposeOneTreatment = !!config.purposeOneTreatment;
  if (config.publisherCC) {
    model.publisherCountryCode = config.publisherCC.slice(0, 2).toUpperCase();
  }

  // Only signal what was actually disclosed: never a consent bit for a purpose no
  // vendor asks consent for, never an LI-transparency bit for a purpose no vendor
  // relies on LI for (and never for purposes 1, 3–6), never a vendor LI bit for a
  // vendor without LI purposes (TCF v2.4: no Special-Purpose-only workaround).
  const clean = sanitizeState(config, state);

  setVector(model.purposeConsents, clean.purposeConsents);
  setVector(model.purposeLegitimateInterests, clean.purposeLegitimateInterests);
  setVector(model.vendorConsents, clean.vendorConsents);
  setVector(model.vendorLegitimateInterests, clean.vendorLegitimateInterests);
  setVector(model.specialFeatureOptins, clean.specialFeatureOptins);

  // Disclosed Vendors (mandatory for service-specific / TCF 2.2+)
  model.setAllVendorsDisclosed();

  // Publisher Restrictions
  for (const row of config.publisherRestrictions || []) {
    const type = RESTRICTION_MAP[String(row.restriction_type).toLowerCase()];
    if (type === undefined) continue;
    const pr = new PurposeRestriction(Number(row.purpose_id), type);
    model.publisherRestrictions.add(Number(row.vendor_id), pr);
  }

  return TCString.encode(model);
}

export function decodeTcString(tc: string) {
  return TCString.decode(tc);
}

function setVector(vector: { set: (id: number) => void; unset: (id: number) => void }, map: Record<number, boolean>) {
  Object.entries(map).forEach(([id, allowed]) => {
    if (allowed) vector.set(+id); else vector.unset(+id);
  });
}

export function toGvlJson(config: CmpConfigPayload) {
  const purposes: Record<string, unknown> = {};
  for (const p of config.gvl.purposes) {
    purposes[String(p.id)] = {
      id: p.id, name: p.name, description: p.description || "",
      illustrations: p.illustrations || []
    };
  }
  const specialPurposes: Record<string, unknown> = {};
  for (const p of config.gvl.specialPurposes || []) {
    specialPurposes[String(p.id)] = {
      id: p.id, name: p.name, description: p.description || "",
      illustrations: p.illustrations || []
    };
  }
  const features: Record<string, unknown> = {};
  for (const f of config.gvl.features || []) {
    features[String(f.id)] = {
      id: f.id, name: f.name, description: f.description || "",
      illustrations: f.illustrations || []
    };
  }
  const specialFeatures: Record<string, unknown> = {};
  for (const f of config.gvl.specialFeatures || []) {
    specialFeatures[String(f.id)] = {
      id: f.id, name: f.name, description: f.description || "",
      illustrations: f.illustrations || []
    };
  }
  const stacks: Record<string, unknown> = {};
  for (const s of config.gvl.stacks || []) {
    stacks[String(s.id)] = {
      id: s.id, name: s.name, description: s.description || "",
      purposes: s.purposes || [], specialFeatures: s.specialFeatures || []
    };
  }
  const vendors: Record<string, unknown> = {};
  for (const v of config.gvl.vendors) {
    vendors[String(v.id)] = {
      id: v.id, name: v.name,
      purposes: v.purposes || [],
      legIntPurposes: (v.legIntPurposes || []).filter((id) => !NO_LI_PURPOSE_IDS.has(id)),
      flexiblePurposes: v.flexiblePurposes || [],
      specialPurposes: v.specialPurposes || [],
      features: v.features || [],
      specialFeatures: v.specialFeatures || [],
      policyUrl: v.policyUrl || "https://example.com/privacy",
      urls: v.urls || [],
      dataRetention: v.dataRetention || {},
      dataDeclaration: v.dataDeclaration || [],
      usesCookies: !!v.usesCookies,
      cookieMaxAgeSeconds: v.cookieMaxAgeSeconds == null ? null : Number(v.cookieMaxAgeSeconds),
      cookieRefresh: !!v.cookieRefresh,
      usesNonCookieAccess: !!v.usesNonCookieAccess,
      deviceStorageDisclosureUrl: v.deviceStorageDisclosureUrl || ""
    };
  }
  // Prefer GVL policy version; fall back to 5 (Policies 5.0 / TCF 2.2+)
  const policyVersion = config.gvl.tcfPolicyVersion && config.gvl.tcfPolicyVersion >= 4
    ? Math.max(config.gvl.tcfPolicyVersion, 5)
    : 5;

  return {
    gvlSpecificationVersion: 3,
    vendorListVersion: config.gvl.vendorListVersion,
    tcfPolicyVersion: policyVersion,
    lastUpdated: new Date().toISOString(),
    purposes, specialPurposes, features, specialFeatures, stacks, vendors
  };
}

/**
 * Removes every signal that was not disclosed / is not used by the configured
 * vendors (after publisher restrictions). Applied before encoding any TC string.
 */
export function sanitizeState(config: CmpConfigPayload, state: ConsentState): ConsentState {
  const d = computeDisclosure(config);
  const out = emptyState(config);
  out.customVendors = { ...out.customVendors, ...state.customVendors };
  out.customPurposes = { ...out.customPurposes, ...state.customPurposes };
  for (const pid of d.consentPurposeVendors.keys()) out.purposeConsents[pid] = !!state.purposeConsents[pid];
  for (const pid of d.liPurposeVendors.keys()) {
    out.purposeLegitimateInterests[pid] = !NO_LI_PURPOSE_IDS.has(pid) && !!state.purposeLegitimateInterests[pid];
  }
  for (const vid of d.consentVendorIds) out.vendorConsents[vid] = !!state.vendorConsents[vid];
  for (const vid of d.liVendorIds) out.vendorLegitimateInterests[vid] = !!state.vendorLegitimateInterests[vid];
  for (const sf of d.specialFeatureVendors.keys()) out.specialFeatureOptins[sf] = !!state.specialFeatureOptins[sf];
  return out;
}

export function acceptAllState(config: CmpConfigPayload): ConsentState {
  const d = computeDisclosure(config);
  const state = emptyState(config);
  for (const pid of d.consentPurposeVendors.keys()) state.purposeConsents[pid] = true;
  for (const pid of d.liPurposeVendors.keys()) state.purposeLegitimateInterests[pid] = !NO_LI_PURPOSE_IDS.has(pid);
  for (const vid of d.consentVendorIds) state.vendorConsents[vid] = true;
  for (const vid of d.liVendorIds) state.vendorLegitimateInterests[vid] = true;
  for (const sf of d.specialFeatureVendors.keys()) state.specialFeatureOptins[sf] = true;
  for (const cv of config.customVendors || []) state.customVendors[cv.key] = true;
  for (const cp of config.customPurposes || []) state.customPurposes[cp.key] = true;
  return state;
}

/**
 * Pre-selection for the settings layer before any choice was made: every consent
 * and special-feature toggle is off (Appendix B C(d)); legitimate-interest toggles
 * are on (transparency is established; the user objects by switching them off).
 */
export function defaultChoicesState(config: CmpConfigPayload): ConsentState {
  const d = computeDisclosure(config);
  const state = rejectAllState(config);
  for (const pid of d.liPurposeVendors.keys()) state.purposeLegitimateInterests[pid] = !NO_LI_PURPOSE_IDS.has(pid);
  for (const vid of d.liVendorIds) state.vendorLegitimateInterests[vid] = true;
  return state;
}

/**
 * "Reject all" / "Withdraw all": no consent, no special feature opt-in and an
 * objection to all legitimate-interest processing (the button says "all").
 */
export function rejectAllState(config: CmpConfigPayload): ConsentState {
  const state = emptyState(config);
  for (const cp of config.customPurposes || []) {
    if (cp.necessary) state.customPurposes[cp.key] = true;
  }
  for (const cv of config.customVendors || []) {
    if (cv.legalBasis === "necessary") state.customVendors[cv.key] = true;
  }
  return state;
}

function emptyState(config: CmpConfigPayload): ConsentState {
  const state: ConsentState = {
    purposeConsents: {}, purposeLegitimateInterests: {},
    vendorConsents: {}, vendorLegitimateInterests: {},
    specialFeatureOptins: {}, customVendors: {}, customPurposes: {}
  };
  for (const p of config.gvl.purposes) {
    state.purposeConsents[p.id] = false;
    state.purposeLegitimateInterests[p.id] = false;
  }
  for (const v of config.gvl.vendors) {
    state.vendorConsents[v.id] = false;
    state.vendorLegitimateInterests[v.id] = false;
  }
  for (const f of config.gvl.specialFeatures || []) state.specialFeatureOptins[f.id] = false;
  for (const cv of config.customVendors || []) state.customVendors[cv.key] = false;
  for (const cp of config.customPurposes || []) state.customPurposes[cp.key] = !!cp.necessary;
  return state;
}
